<template>
  <l-modal
    size="small"
    :auto-height="true"
    :title="t('link-customer.title')"
    :button="selectedOption === 'manual'"
    :button-enabled="identifier.length > 0"
    :button-text="t('link-customer.save')"
    :button-loading="loading"
    @close="$emit('close')"
    @action="save(identifier)"
  >
    <div class="flex flex-col justify-center">
      <template v-if="deviceHasCamera">
        <l-option-selector v-model="selectedOption" :options="selectOptions" class="w-full mb-4" />
      </template>
      <div v-if="selectedOption === 'manual'" class="w-full overflow-y-scroll scrolling-touch">
        <l-field
          :label="isHotel ? t('link-customer.identifier') : t('link-customer.phone-number')"
          class="w-full"
        >
          <l-input
            v-if="isHotel"
            v-model="identifier"
            :placeholder="t('link-customer.type-identifier')"
          />
          <l-phone-input
            v-else
            ref="identifierInput"
            v-model="identifier"
            :icon="'phone'"
            :placeholder="t('link-customer.type-phone-number')"
          />
        </l-field>
      </div>

      <div
        v-if="selectedOption === 'automatic' && deviceHasCamera"
        class="relative w-full mt-4 flex flex-col items-center"
      >
        <q-r-scanner
          @found="onQrFound"
          @close="$emit('close')"
          @cancel="activateManualInput()"
          @error="(activateManualInput(), (deviceCameraError = true))"
        />
        <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-n-800/50">
          <l-icon name="status" class="animate-spin text-n-0" size="large" />
        </div>
      </div>
    </div>
  </l-modal>
</template>

<script setup lang="ts">
import { Capacitor } from '@capacitor/core'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

import {
  LField,
  LIcon,
  LInput,
  LModal,
  LOptionSelector,
  LPhoneInput,
  useTracker
} from '@last/core-ui/paprika'

import { tabsApi } from '@/api/client'
import { useToast } from '@/plugins/toast/toast'
import { useConfigStore } from '@/store/config'

import QRScanner from './QRScanner.vue'

const props = defineProps<{
  tabId: string
}>()

const { t } = useI18n()
const tracker = useTracker()

const emit = defineEmits<{
  close: []
}>()

const configStore = useConfigStore()
const { config } = storeToRefs(configStore)
const toast = useToast()

const deviceCameraError = ref(false)
const identifier = ref('')
const loading = ref(false)

const selectOptions = [
  { label: t('link-customer.scan-qr'), value: 'automatic' },
  { label: t('link-customer.phone-number'), value: 'manual' }
]

const selectedOption = ref<'automatic' | 'manual'>('automatic')
const isHotel = computed(() => config.value.isHotel ?? false)
const identifierInput = useTemplateRef('identifierInput')

onMounted(() => {
  if (!deviceHasCamera.value) {
    activateManualInput()
  }
})

const deviceHasCamera = computed(() => {
  const currentPlatform = Capacitor.getPlatform()
  return (currentPlatform === 'android' || currentPlatform === 'ios') && !deviceCameraError.value
})

async function save(value: string) {
  loading.value = true
  try {
    if (isHotel.value) {
      await tabsApi.linkRoomToTab({ tabId: props.tabId, roomName: value })
    } else {
      await tabsApi.linkCustomerToTab({ tabId: props.tabId, phoneNumber: value })
    }
    toast({
      title: t('link-customer.customer-linked'),
      message: t('link-customer.customer-linked-successfully')
    })
    emit('close')
  } catch {
    toast({
      type: 'error',
      title: t('link-customer.error'),
      message: t('link-customer.customer-linked-error')
    })
  }
  loading.value = false
}

function activateManualInput() {
  selectedOption.value = 'manual'
  nextTick(() => {
    identifierInput.value?.focus()
  })
}

function onQrFound(value: string) {
  tracker.track('qr_found')
  save(value)
}
</script>
